import React, { useState } from 'react';
import {
    User, Upload, FileText, Send, Landmark,
    BarChart3, Handshake, Star, MoveRight,
    CheckCircle2, Clock, Lock, MessageSquare, AlertCircle
} from 'lucide-react';
import DashboardMessaging from './DashboardMessaging';
import AdmissionsManager from './AdmissionsManager';

interface AdmissionsHomeProps {
    documents: any[];
    onUpdate: () => void;
    onBack: () => void;
    user: any;
    profile: any;
    tracking: any;
}

const AdmissionsHome: React.FC<AdmissionsHomeProps> = ({ documents, onUpdate, onBack, user, profile, tracking }) => {
    const [view, setView] = useState<'home' | 'docs' | 'profile'>('home');

    // Mock statuses for the flow - in a real app, these would come from the 'tracking' prop
    const steps = [
        { id: 'profile', phase: 1, label: 'Complete Personal Profile', icon: <User size={20} />, status: 'completed' },
        { id: 'docs', phase: 1, label: 'Upload Required Documents', icon: <Upload size={20} />, status: 'completed' },
        { id: 'review', phase: 2, label: 'Review Application', icon: <FileText size={20} />, status: 'completed' },
        { id: 'submit', phase: 2, label: 'Submit Application', icon: <Send size={20} />, status: 'completed' },
        { id: 'uni_submit', phase: 2, label: 'University Submission', icon: <Landmark size={20} />, status: 'in-progress' },
        { id: 'track', phase: 3, label: 'Track University Decisions', icon: <BarChart3 size={20} />, status: 'pending' },
        { id: 'offers', phase: 3, label: 'Manage Offers', icon: <Handshake size={20} />, status: 'pending' },
        { id: 'confirm', phase: 3, label: 'Confirm Final University Choice', icon: <Star size={20} />, status: 'pending' },
        { id: 'visa', phase: 3, label: 'Move to Visa Lab', icon: <MoveRight size={20} />, status: 'pending' },
    ];

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed': return <CheckCircle2 size={12} className="text-emerald-500" />;
            case 'in-progress': return <Clock size={12} className="text-amber-500" />;
            case 'pending': return <div className="w-3 h-3 rounded-full border-2 border-slate-300" />;
            default: return null;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'border-emerald-200 bg-emerald-50 text-emerald-700';
            case 'in-progress': return 'border-[#009B8F] bg-[#009B8F]/5 text-[#009B8F] shadow-sm';
            case 'pending': return 'border-gray-200 bg-white text-gray-400 opacity-60';
            default: return 'border-gray-200 bg-white text-gray-400';
        }
    };

    if (view === 'docs') {
        return <AdmissionsManager documents={documents} onUpdate={onUpdate} onBack={() => setView('home')} />;
    }

    if (view === 'profile') {
        return (
            <div className="space-y-8 animate-fadeIn max-w-5xl mx-auto">
                <button
                    onClick={() => setView('home')}
                    className="flex items-center gap-2 text-xs font-bold text-[#009B8F] uppercase tracking-widest hover:translate-x-[-4px] transition-all"
                >
                    ← Back to Admissions Home
                </button>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10">
                    <h3 className="text-3xl font-serif font-bold text-slate-800 mb-8">Personal Profile</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {[
                            { label: 'Full Name', value: user?.FullName },
                            { label: 'Email Address', value: user?.Email },
                            { label: 'Phone Number', value: user?.Phone },
                            { label: 'Target Country', value: user?.Country || profile?.TargetCountry },
                            { label: 'Intended Course', value: profile?.TargetCourse },
                            { label: 'Degree Level', value: profile?.DegreeLevel },
                        ].map((item, idx) => (
                            <div key={idx} className="p-5 bg-gray-50 rounded-xl border border-gray-200">
                                <label className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] block mb-2">{item.label}</label>
                                <p className="font-bold text-slate-800">{item.value || 'Not Specified'}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fadeIn">
            {/* Admissions Header — dark banner matching homepage feel */}
            <div className="relative overflow-hidden rounded-2xl bg-[#011E26] p-8 md:p-10 shadow-lg">
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <div className="inline-flex items-center gap-2 bg-[#009B8F]/20 px-3 py-1 rounded border border-[#009B8F]/30 mb-4">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#009B8F] animate-pulse"></div>
                            <span className="text-[10px] font-bold tracking-widest text-[#009B8F] uppercase">Student Portal</span>
                        </div>
                        <h2 className="text-4xl font-serif font-bold text-white mb-3 tracking-tight">Admissions Hub</h2>
                        <p className="text-slate-400 text-sm max-w-xl leading-relaxed italic">
                            Your universal gateway to top-tier universities. Track your progress from profile creation to final acceptance in real-time.
                        </p>
                    </div>

                    {/* Status Summary */}
                    <div className="bg-white/10 border border-white/10 p-5 rounded-2xl flex items-center gap-4 flex-shrink-0">
                        <div className="bg-[#009B8F] p-3 rounded-xl">
                            <AlertCircle size={24} className="text-white" />
                        </div>
                        <div>
                            <p className="text-[#009B8F] text-[10px] font-bold uppercase tracking-widest mb-1">Live Tracking</p>
                            <p className="text-white text-sm font-bold">3 Submitted <span className="text-white/30 mx-2">|</span> 1 Action</p>
                        </div>
                    </div>
                </div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#009B8F]/10 rounded-full -mr-20 -mt-20 pointer-events-none"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: Advisor Support Card */}
                <div className="lg:col-span-1 order-2 lg:order-1">
                    <div className="bg-white border border-gray-100 shadow-sm p-6 rounded-2xl flex flex-col h-full sticky top-24">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-lg font-serif font-bold text-slate-800">Nexus Advisors</h3>
                            <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                <span className="text-[8px] font-bold text-emerald-600 uppercase tracking-widest">Online</span>
                            </div>
                        </div>
                        <DashboardMessaging context="admissions" />
                        <div className="mt-6 pt-4 border-t border-gray-100">
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest text-center">Global Expert Network</p>
                        </div>
                    </div>
                </div>

                {/* Right: Universal Flow */}
                <div className="lg:col-span-2 space-y-10 order-1 lg:order-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <h3 className="text-2xl font-serif font-bold text-slate-800">Universal Admissions Flow</h3>
                        <div className="flex gap-3 p-2 bg-gray-50 rounded-xl border border-gray-200">
                            {[
                                { color: 'bg-emerald-500', label: 'Done' },
                                { color: 'bg-[#009B8F]', label: 'Active' },
                                { color: 'bg-gray-300', label: 'Locked' }
                            ].map(l => (
                                <div key={l.label} className="flex items-center gap-2 px-3 py-1.5">
                                    <div className={`w-2 h-2 rounded-full ${l.color}`} />
                                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-widest">{l.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {[1, 2, 3].map(phase => (
                        <div key={phase} className="space-y-5">
                            <div className="flex items-center gap-4">
                                <div className="bg-[#009B8F] text-white text-xs font-bold w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0">0{phase}</div>
                                <h4 className="text-[13px] text-slate-600 font-bold uppercase tracking-widest">
                                    {phase === 1 ? 'Preparation & Profile' : phase === 2 ? 'Submission & Review' : 'Decisions & Enrollment'}
                                </h4>
                                <div className="flex-1 h-px bg-gray-200"></div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                                {steps.filter(s => s.phase === phase).map(step => (
                                    <div
                                        key={step.id}
                                        onClick={() => {
                                            if (step.id === 'docs') setView('docs');
                                            if (step.id === 'profile') setView('profile');
                                        }}
                                        className={`
                                            group relative p-5 rounded-2xl transition-all duration-300 cursor-pointer border-2
                                            ${step.status === 'in-progress'
                                                ? 'bg-[#009B8F]/5 border-[#009B8F] shadow-sm'
                                                : step.status === 'completed'
                                                    ? 'bg-white border-emerald-200 hover:border-[#009B8F]/50 hover:shadow-sm'
                                                    : 'bg-gray-50 border-gray-200 opacity-50 hover:opacity-80'}
                                        `}
                                    >
                                        <div className="absolute top-4 right-4">
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${step.status === 'completed' ? 'bg-emerald-500 border-emerald-500' : step.status === 'in-progress' ? 'border-[#009B8F]' : 'border-gray-300'}`}>
                                                {step.status === 'completed' && <CheckCircle2 size={12} className="text-white" />}
                                                {step.status === 'in-progress' && <div className="w-2 h-2 rounded-full bg-[#009B8F]"></div>}
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${step.status === 'in-progress' ? 'bg-[#009B8F] text-white' : step.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                                                {React.cloneElement(step.icon as React.ReactElement, { size: 22 })}
                                            </div>
                                        </div>

                                        <p className={`text-sm font-bold leading-tight ${step.status === 'in-progress' ? 'text-[#009B8F]' : step.status === 'completed' ? 'text-slate-700' : 'text-slate-400'}`}>
                                            {step.label}
                                        </p>

                                        <div className="mt-3">
                                            <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
                                                <div className={`h-full rounded-full transition-all duration-700 ${step.status === 'in-progress' ? 'w-1/2 bg-[#009B8F]' : step.status === 'completed' ? 'w-full bg-emerald-400' : 'w-0'}`} />
                                            </div>
                                            <span className={`text-[9px] font-bold uppercase tracking-wider mt-1 block ${step.status === 'in-progress' ? 'text-[#009B8F]' : step.status === 'completed' ? 'text-emerald-500' : 'text-slate-400'}`}>
                                                {step.status === 'in-progress' ? 'In Progress' : step.status === 'completed' ? 'Completed' : 'Pending'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Global Verification Bar */}
            <div className="bg-white border border-gray-100 shadow-sm p-7 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                    <div className="flex -space-x-3">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="w-10 h-10 rounded-xl bg-[#011E26]/10 border-2 border-white flex items-center justify-center text-xs font-bold text-[#011E26] shadow-sm">
                                {String.fromCharCode(64 + i)}
                            </div>
                        ))}
                    </div>
                    <div>
                        <p className="text-sm font-bold text-slate-800 uppercase tracking-widest mb-1">Global Admissions Network</p>
                        <p className="text-xs text-slate-500 italic">Verified across 1,200+ elite institutions worldwide</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-3 px-5 py-3 bg-emerald-50 rounded-xl border border-emerald-200">
                        <CheckCircle2 size={16} className="text-emerald-600" />
                        <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">Verified Profile</span>
                    </div>
                    <div className="flex items-center gap-3 px-5 py-3 bg-amber-50 rounded-xl border border-amber-200">
                        <Clock size={16} className="text-amber-600" />
                        <span className="text-[11px] font-bold text-amber-700 uppercase tracking-widest">Awaiting Review</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdmissionsHome;
